/* istanbul ignore file */
import React, { MouseEventHandler } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';
import { isEmpty } from 'lodash';
import SahamDrawer from 'components/SahamDrawer';
import { translate } from 'containers/services/translate';
import { ASSIGN_EXPERT, EMPTY_ASSIGN_EXPERT } from 'containers/data-structure';
import messages from 'containers/CourtManagerLegalProceedings/messages';
import { ContainerInDrawer } from 'containers/LawyerFeeNote/styled-components';
import AssignExpertForm from 'containers/CourtManagerLegalProceedings/AssignExpert';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import * as util from 'containers/RecapManagerPage/utils';
import { LISTINGS } from 'containers/data-structure';
import { get, uniqueId } from 'lodash';
import { SagisConfig } from 'containers/AppContainer/typings';
import SahamDialog from 'components/SahamDialog';
import { ITEM_KEYS, SAGIS_KEYS } from 'containers/RecapManagerPage/constants';
import {
  extractCourtFileNumber,
  returnDefaultCodeByType,
  returnReserveCodeByType,
} from 'containers/CourtManagerLegalProceedings/AssignExpert/utils';
import currentUserStorage from 'containers/services/security/current-user-storage';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';

export interface AssignExpertDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: Function;
  listings: any;
  victims: any;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  setDocumentId: Function;
  documentId: string;
  requestAssignedExpert: Function;
  claimId: number;
  claimNumber: string;
  judiciaryProcessId: number;
  courtFileNumber: string;
  requestCorporealLawyers: Function;
  corporealLawyers: any;
  assignedLawyer: any;
  searchQuery: any;
  civilProceeding: any;
  corporealExperts: any;
  requestGetExpertUsers: Function;
  vehicles: any;
  requestGetCivilProceeding: Function;
  selectedSearchResult: any;
  claimHasCivilPartyJudiciaryProcess: any;
  riskKey: string;
  claimRecap: any;
}

const AssignExpertDrawer: React.FC<AssignExpertDrawerProps> = (
  props,
): JSX.Element => {
  const [assignExpert, setAssignExpert] = React.useState({
    ...EMPTY_ASSIGN_EXPERT,
    lawyer: props.assignedLawyer,
    procedureNatureId: props.searchQuery?.procedureNatureId,
    step: props.searchQuery?.jurisdictionDegreeId,
    courtFileNumber: props.searchQuery?.courtNumber,
    city: props.searchQuery?.jurisdiction,
  });
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [invitationDoc, setInvitationDoc] = React.useState<null | any>({});
  const [stakeholders, setStakeholders] = React.useState([] as SagisConfig[]);

  React.useEffect(() => {
    const _stackholders = util.getListBySagisKey(
      get(props.listings, LISTINGS.STACKHOLDER_TYPE, []),
      get(props.listings, LISTINGS.STACKHOLDERS, []),
      ITEM_KEYS.LAWYER,
      SAGIS_KEYS.ITEM_TYPE as keyof SagisConfig,
    );

    const lawyers = util.getBawabaLawyers(
      _stackholders,
      props.corporealLawyers,
    );
    setStakeholders(lawyers);
    if (isEmpty(props.civilProceeding)) {
      props.requestGetCivilProceeding(props.claimId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.listings, props.civilProceeding]);

  const correctionnelProceeding = props.civilProceeding?.filter(
    (element: { procedureNatureId: number }) => element.procedureNatureId === 3,
  );
  const handleValidation = () => {
    props.requestAssignedExpert(
      {
        ...assignExpert,
        document: {
          ...invitationDoc,
          isIndexed: false,
          uploadedBy: currentUserStorage.get().username,
        },
        missionDate:
          get(assignExpert, 'missionDate') === '' ||
          get(assignExpert, 'missionDate') === 'Invalid date'
            ? null
            : get(assignExpert, 'missionDate'),
        receiptDate:
          get(assignExpert, 'receiptDate') === '' ||
          get(assignExpert, 'receiptDate') === 'Invalid date'
            ? null
            : get(assignExpert, 'receiptDate'),
        courtFileNumber: get(assignExpert, 'judiciaryProcessId')
          ? extractCourtFileNumber(
              props.civilProceeding,
              get(assignExpert, 'judiciaryProcessId'),
            )
          : props.courtFileNumber,
        claimId: props.claimId,
        intervenantSagis: get(assignExpert, 'assignedExperts').map(
          (element: any) => ({
            intervenantTypeCode: returnDefaultCodeByType(
              Number(get(assignExpert, 'type')),
            ),
            intervenantCode: element.expertCode,
          }),
        ),
        assignementDetails: {
          contractorFees: get(assignExpert, 'assignedExperts').map(
            (element: any) => ({
              uniqueId: uniqueId(),
              contractorTypeCode: returnDefaultCodeByType(
                Number(get(assignExpert, 'type')),
              ),
              contractorCode: element.expertCode,
              amount: 0,
              isPayment: false,
            }),
          ),
          uuid: uuidV4(),
        },
        reserve: returnReserveCodeByType(
          Number(get(assignExpert, 'type')),
          props.listings,
        ),
        claimNumber: props.claimNumber,
        isTrackingActive: featureService.hasFeature(
          FEATURES.TRACKING_ACTIONS_USER,
        ),
        riskKey: props.riskKey,
        createdBy: currentUserStorage?.get().username,
        judiciaryProcessId: get(assignExpert, 'judiciaryProcessId', '')
          ? get(assignExpert, 'judiciaryProcessId')
          : get(correctionnelProceeding, '[0].id'),
      },
      successCallBack,
    );
    props.handleToggleDrawer();
  };

  const successCallBack = () => {
    toast.success(translate(messages.successTitleAssign, props));
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };
  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      title={translate({ ...messages.ExpertDrawerTitle }, props, {
        number: null,
      })}
      handleValidateDrawer={handleOpenDialog}
      confirmButtonTitle={translate(messages.AssignButton, props)}
      isSaveButtonDisabled={
        !invitationDoc?.url ||
        !invitationDoc?.type ||
        get(assignExpert, [ASSIGN_EXPERT.MISSION_DATE], '') ===
          'Invalid date' ||
        get(assignExpert, [ASSIGN_EXPERT.RECEIPT_DATE], '') === 'Invalid date'
      }
    >
      <ContainerInDrawer>
        <AssignExpertForm
          assignExpert={assignExpert}
          listings={props.listings}
          setAssignExpertFields={setAssignExpert}
          victims={props.victims}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          resetUploadedDocument={props.resetUploadedDocument}
          setDocumentId={props.setDocumentId}
          documentId={props.documentId}
          invitationDoc={invitationDoc}
          stakeholders={stakeholders}
          corporealLawyers={props.corporealLawyers}
          setInvitationDoc={setInvitationDoc}
          corporealExperts={props.corporealExperts}
          requestGetExpertUsers={props.requestGetExpertUsers}
          vehicles={props.vehicles}
          civilProceeding={props.civilProceeding}
          isUpdate={false}
          claimHasCivilPartyJudiciaryProcess={
            props.claimHasCivilPartyJudiciaryProcess
          }
          claimRecap={props.claimRecap}
        />
      </ContainerInDrawer>
      <SahamDialog
        handleValidate={handleValidation}
        handleClose={handleCloseDialog as MouseEventHandler}
        title={translate(messages.courtManagerConfirmationDialogTitle, props)}
        cancelTitle={translate(
          messages.courtManagerConfirmationDialogCancel,
          props,
        )}
        confirmTitle={translate(
          messages.courtManagerConfirmationDialogConfirm,
          props,
        )}
        open={isDialogOpen}
      >
        {translate(messages.courtManagerConfirmationDialogContent, props)}
      </SahamDialog>
    </SahamDrawer>
  );
};

export default injectIntl(AssignExpertDrawer);
